/**
 * Seed data — Accounts (real data from the Client Lifecycle CSV) and
 * AppUsers only. People (contacts + prospects) ship empty: they are now
 * entered in-app rather than seeded with mock records.
 *
 * Loaded once on first run via the corresponding `bootstrap…IfEmpty`
 * helper in /accounts.ts and /people.ts.
 */

import type {
  Account,
  AgentRunEntry,
  AppUser,
  AuditEntry,
  BusinessDoc,
  DeployedAgent,
  Person,
  PersonActivity,
  Project,
  ProjectTask,
  RoadmapItem,
  TaskEvent,
  Ticket,
  TicketEvent,
} from './types';

const TS = '2026-04-20T16:00:00.000Z';

// Tiny deterministic id generator so the seed can re-run without
// reshuffling ids across reloads. Real records use crypto.randomUUID.
let _seq = 0;
const sid = (prefix: string) => `${prefix}-${++_seq}`;

// ===========================================================================
// People — intentionally empty. Contacts and prospects are created in-app.
// ===========================================================================

export const seedPeople: Person[] = [];

// ===========================================================================
// Demo people — local/mock-mode ONLY. Never loaded into Firestore.
// ===========================================================================
//
// `seedPeople` ships empty so the live (Firestore) path is never polluted
// with mock contacts. But an empty CRM makes the local dev/demo build feel
// hollow — Contacts/Prospects tabs, lead scoring, follow-up reminders, the
// chat data-answerer and the attention rollup all have nothing to chew on.
//
// `demoPeople` is a realistic roster tied to the seeded company names. It is
// loaded by `bootstrapPeopleIfEmpty()` *only when `useFirestore` is false*,
// so it exists purely for local exploration and disappears the moment real
// Firebase is configured. Dates are anchored around 2026-06 so some
// follow-ups read as overdue and some as due-soon against "today".
//
// Own id counter (`did`) so this block never consumes the shared `_seq`
// that account ids depend on.

let _demoSeq = 0;
const did = (prefix: string) => `${prefix}-${++_demoSeq}`;

const act = (
  at: string,
  kind: PersonActivity['kind'],
  text: string,
  actor = 'You',
): PersonActivity => ({ id: did('act'), at, kind, actor, text });

export const demoPeople: Person[] = [
  // --- Prospects ----------------------------------------------------------
  {
    id: did('per'),
    category: 'prospect',
    name: 'Maria Lopez',
    email: 'maria.lopez@northwind.io',
    phone: '+1 415 555 0142',
    title: 'VP of Growth',
    company: 'Northwind',
    linkedinUrl: 'https://www.linkedin.com/in/maria-lopez-growth',
    website: 'https://northwind.io',
    tags: ['inbound', 'warm'],
    stage: 'qualified',
    source: 'Inbound — website',
    estimatedValue: 120_000,
    score: 78,
    lastTouch: '2026-05-18',
    followUpDate: '2026-05-20', // overdue
    nextStep: 'Send pricing one-pager + book technical scoping call',
    activity: [
      act('2026-05-10T15:00:00.000Z', 'call', 'Discovery call — strong fit, ~50 person GTM team.'),
      act('2026-05-18T17:30:00.000Z', 'email', 'Sent recap + case study; she asked for pricing.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'prospect',
    name: 'Devon Pratt',
    email: 'devon@helixbio.com',
    title: 'Head of RevOps',
    company: 'Helix Bio',
    linkedinUrl: 'https://www.linkedin.com/in/devonpratt',
    website: 'https://helixbio.com',
    tags: ['outbound'],
    stage: 'new',
    source: 'Outbound — LinkedIn',
    estimatedValue: 60_000,
    score: 41,
    lastTouch: '2026-05-30',
    followUpDate: '2026-06-06', // due soon
    nextStep: 'Qualify budget + timeline on intro call',
    activity: [
      act('2026-05-30T16:00:00.000Z', 'email', 'Replied to outbound; open to a 20-min intro.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'prospect',
    name: 'Sara Chen',
    email: 'sara.chen@vantagerobotics.com',
    title: 'COO',
    company: 'Vantage Robotics',
    linkedinUrl: 'https://www.linkedin.com/in/sarachen-coo',
    website: 'https://vantagerobotics.com',
    tags: ['inbound', 'champion'],
    stage: 'proposal',
    source: 'Referral — Drift Peptides',
    estimatedValue: 210_000,
    score: 88,
    lastTouch: '2026-06-01',
    followUpDate: '2026-06-09',
    nextStep: 'Follow up on proposal; address security questionnaire',
    activity: [
      act('2026-05-22T18:00:00.000Z', 'meeting', 'Solution review with exec team — green light to propose.'),
      act('2026-06-01T19:15:00.000Z', 'email', 'Sent proposal v1 ($210k); awaiting security review.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'prospect',
    name: 'Priya Nair',
    email: 'priya@cedarandco.com',
    title: 'Founder',
    company: 'Cedar & Co',
    linkedinUrl: 'https://www.linkedin.com/in/priyanair',
    website: 'https://cedarandco.com',
    tags: ['outbound', 'smb'],
    stage: 'new',
    source: 'Outbound — email',
    estimatedValue: 32_000,
    score: 29,
    lastTouch: '2026-05-15',
    followUpDate: '2026-05-28', // overdue
    nextStep: 'Re-engage; she went quiet after first reply',
    activity: [
      act('2026-05-15T14:00:00.000Z', 'email', 'Initial reply — interested but slammed until June.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'prospect',
    name: 'Marcus Webb',
    email: 'mwebb@lumenhealth.com',
    title: 'Director of Marketing',
    company: 'Lumen Health',
    linkedinUrl: 'https://www.linkedin.com/in/marcuswebb',
    website: 'https://lumenhealth.com',
    tags: ['inbound'],
    stage: 'qualified',
    source: 'Inbound — webinar',
    estimatedValue: 95_000,
    score: 64,
    lastTouch: '2026-05-27',
    followUpDate: '2026-06-12',
    nextStep: 'Loop in their VP of Sales for buy-in',
    activity: [
      act('2026-05-27T16:45:00.000Z', 'call', 'Qualified — needs VP sign-off, evaluating two vendors.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'prospect',
    name: 'Tom Ellison',
    email: 'tom@greylockmedia.com',
    title: 'CMO',
    company: 'Greylock Media',
    tags: ['outbound'],
    stage: 'closed-lost',
    source: 'Outbound — LinkedIn',
    estimatedValue: 48_000,
    score: 12,
    lastTouch: '2026-04-30',
    nextStep: 'Lost to in-house build; revisit Q4',
    activity: [
      act('2026-04-30T15:00:00.000Z', 'note', 'Chose to build in-house. Nurture for Q4 re-eval.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'prospect',
    name: 'Jordan Riggs',
    email: 'jordan@brightline.ai',
    title: 'VP Sales',
    company: 'Brightline',
    linkedinUrl: 'https://www.linkedin.com/in/jordanriggs',
    website: 'https://brightline.ai',
    tags: ['inbound', 'champion'],
    stage: 'closed-won',
    source: 'Referral',
    estimatedValue: 150_000,
    score: 92,
    lastTouch: '2026-05-29',
    nextStep: 'Hand off to implementation; kickoff scheduled',
    activity: [
      act('2026-05-29T20:00:00.000Z', 'meeting', 'Signed! $150k/yr. Kickoff booked for mid-June.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },

  // --- Customers ----------------------------------------------------------
  {
    id: did('per'),
    category: 'customer',
    name: 'Lena Hart',
    email: 'lena.hart@driftpeptides.com',
    phone: '+1 312 555 0190',
    title: 'Head of Content',
    company: 'Drift Peptides',
    tags: ['enterprise', 'advocate'],
    status: 'active',
    accountValue: 240_000,
    contractStart: '2025-09-01',
    renewalDate: '2026-09-01',
    ownerName: 'Adam Bolton',
    nextStep: 'Prep QBR deck for August renewal conversation',
    followUpDate: '2026-06-18',
    deliverables: [
      { id: did('del'), title: 'SEC-compliant blog engine', status: 'delivered', dueDate: '2026-03-15' },
      { id: did('del'), title: 'Creator discovery pipeline', status: 'in-progress', dueDate: '2026-06-30' },
      { id: did('del'), title: 'Analytics feedback loop', status: 'planned', dueDate: '2026-08-15' },
    ],
    billingDates: [
      { id: did('bil'), label: 'Q2 invoice', date: '2026-06-01', amount: 60_000, status: 'invoiced' },
      { id: did('bil'), label: 'Q3 invoice', date: '2026-09-01', amount: 60_000, status: 'scheduled' },
    ],
    pocs: [
      { id: did('poc'), name: 'Lena Hart', title: 'Head of Content', email: 'lena.hart@driftpeptides.com', primary: true },
      { id: did('poc'), name: 'Raj Patel', title: 'VP Eng', email: 'raj@driftpeptides.com' },
    ],
    activity: [
      act('2026-05-12T17:00:00.000Z', 'meeting', 'Monthly sync — blog engine driving 30% more inbound.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'customer',
    name: 'Carlos Mendez',
    email: 'carlos@karmic.com',
    title: 'Founder & CEO',
    company: 'Karmic',
    tags: ['mid-market'],
    status: 'active',
    accountValue: 96_000,
    contractStart: '2026-01-15',
    renewalDate: '2027-01-15',
    ownerName: 'Grant Walker',
    nextStep: 'Ship CQS optimizer v2; gather expansion signals',
    followUpDate: '2026-06-10',
    deliverables: [
      { id: did('del'), title: 'CQS optimizer', status: 'in-progress', dueDate: '2026-06-20' },
    ],
    billingDates: [
      { id: did('bil'), label: 'Monthly', date: '2026-06-15', amount: 8_000, status: 'scheduled' },
    ],
    pocs: [
      { id: did('poc'), name: 'Carlos Mendez', title: 'Founder & CEO', email: 'carlos@karmic.com', primary: true },
    ],
    activity: [
      act('2026-05-20T16:30:00.000Z', 'call', 'Happy with results; floated a second workflow.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'customer',
    name: 'Aisha Khan',
    email: 'aisha@clinicalfitness.com',
    title: 'Operations Lead',
    company: 'Clinical Fitness',
    tags: ['at-risk'],
    status: 'paused',
    accountValue: 54_000,
    contractStart: '2025-11-01',
    renewalDate: '2026-11-01',
    ownerName: 'Patrick Walker',
    nextStep: 'Re-engage — billing automation paused during their reorg',
    followUpDate: '2026-05-25', // overdue
    deliverables: [
      { id: did('del'), title: 'Monthly billing automation', status: 'blocked', dueDate: '2026-06-05' },
    ],
    billingDates: [
      { id: did('bil'), label: 'Q2 invoice', date: '2026-06-01', amount: 13_500, status: 'overdue' },
    ],
    pocs: [
      { id: did('poc'), name: 'Aisha Khan', title: 'Operations Lead', email: 'aisha@clinicalfitness.com', primary: true },
    ],
    activity: [
      act('2026-05-08T15:00:00.000Z', 'note', 'Reorg internally — asked to pause for 4–6 weeks.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'customer',
    name: 'Ben Foster',
    email: 'ben.foster@bronwick.com',
    title: 'Talent Director',
    company: 'Bronwick',
    tags: ['mid-market', 'advocate'],
    status: 'active',
    accountValue: 132_000,
    contractStart: '2025-12-01',
    renewalDate: '2026-12-01',
    ownerName: 'Adam Bolton',
    nextStep: 'Expand candidate monitoring to second region',
    followUpDate: '2026-06-22',
    deliverables: [
      { id: did('del'), title: 'Candidate monitoring', status: 'delivered', dueDate: '2026-04-01' },
      { id: did('del'), title: 'Prospecting workflow', status: 'in-progress', dueDate: '2026-07-01' },
    ],
    billingDates: [
      { id: did('bil'), label: 'Q3 invoice', date: '2026-07-01', amount: 33_000, status: 'scheduled' },
    ],
    pocs: [
      { id: did('poc'), name: 'Ben Foster', title: 'Talent Director', email: 'ben.foster@bronwick.com', primary: true },
      { id: did('poc'), name: 'Dana Cole', title: 'Recruiting Ops', email: 'dana@bronwick.com' },
    ],
    activity: [
      act('2026-05-25T18:00:00.000Z', 'meeting', 'Strong NPS; discussing a second-region expansion.'),
    ],
    createdAt: TS,
    updatedAt: TS,
  },

  // --- Staff --------------------------------------------------------------
  {
    id: did('per'),
    category: 'staff',
    name: 'Riley Sun',
    email: 'riley@argoiq.com',
    title: 'Sales Development Rep',
    company: 'Argo IQ',
    tags: ['sdr'],
    role: 'SDR',
    team: 'Sales',
    startDate: '2026-02-01',
    location: 'Remote — Austin, TX',
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: did('per'),
    category: 'staff',
    name: 'Noah Park',
    email: 'noah@argoiq.com',
    title: 'Implementation Lead',
    company: 'Argo IQ',
    tags: ['delivery'],
    role: 'Implementation Lead',
    team: 'Delivery',
    startDate: '2025-10-15',
    location: 'Remote — Denver, CO',
    createdAt: TS,
    updatedAt: TS,
  },
];

// ===========================================================================
// Accounts — sourced from `Argo IQ - Client Tracker - Client Lifecycle.csv`
// ===========================================================================
//
// The CSV is workflow-shaped (one row per workflow), but the new schema
// is account-shaped: a single record per company with its workflows as
// a sub-list. Stage assignments are inferred from the free-form
// `Current Status` column and the dated check-marks in the lifecycle
// columns; everything else is editable in-app once it loads.

const auditCreated = (text = 'Account created'): AuditEntry[] => [
  { id: sid('aud'), at: TS, actor: 'System', text },
];

export const seedAccounts: Account[] = [
  // Drift Peptides — mature engagement, mostly in iterative improvement,
  // with platform-integration still earlier. Different per-workflow stages.
  {
    id: sid('acct'),
    name: 'Drift Peptides',
    ownerName: 'Adam Bolton',
    pocName: 'Lena Hart',
    pocTitle: 'Head of Content',
    pocEmail: 'lena.hart@driftpeptides.com',
    pocPhone: '+1 312 555 0190',
    secondaryContacts: [
      { id: sid('poc'), name: 'Raj Patel', title: 'VP Eng', email: 'raj@driftpeptides.com' },
    ],
    workflows: [
      {
        id: sid('wf'),
        name: 'Creator Discovery',
        status: 'Delivery working in hybrid',
        stage: 'Iterative Improvement',
      },
      {
        id: sid('wf'),
        name: 'Content Ideation',
        status: 'Waiting for creators',
        stage: 'Iterative Improvement',
      },
      {
        id: sid('wf'),
        name: 'Analytics Feedback Loop',
        status: 'Waiting for data',
        stage: 'Customer Feedback',
      },
      {
        id: sid('wf'),
        name: 'Platform Integration',
        status: 'Pending testing of IH API',
        stage: '2nd Iteration',
      },
    ],
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: "Sam's List",
    ownerName: 'Grant Walker',
    workflows: [
      {
        id: sid('wf'),
        name: 'SEC Compliant Blog Generation',
        status: 'Delivery working in hybrid, pending v2',
        stage: 'Iterative Improvement',
      },
      {
        id: sid('wf'),
        name: 'Client Outreach System',
        status: 'Engineering this week',
        stage: '2nd Iteration',
      },
    ],
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'Karmic',
    ownerName: 'Grant Walker',
    pocName: 'Carlos Mendez',
    pocTitle: 'Founder & CEO',
    pocEmail: 'carlos@karmic.com',
    workflows: [
      {
        id: sid('wf'),
        name: 'CQS Optimizer',
        status: 'Pending engineering for monitoring',
        stage: '2nd Iteration',
      },
    ],
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'Optimal Data',
    ownerName: 'Grant Walker',
    pocName: 'Paul',
    workflows: [
      {
        id: sid('wf'),
        name: 'LinkedIn Content Engine',
        status: 'Pending feedback from Paul',
        stage: 'Customer Data Shared',
      },
    ],
    notes: 'Customer data shared 4/2.',
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'DevSignal',
    ownerName: 'Trenton Hughes',
    pocName: 'Moummar',
    workflows: [
      {
        id: sid('wf'),
        name: 'X Parody Persona: POps',
        status: 'Pending feedback from Moummar & team',
        stage: 'Customer Feedback',
      },
      {
        id: sid('wf'),
        name: 'X Parody Persona: Engineer',
        status: 'Pending feedback from Moummar & team',
        stage: 'Customer Feedback',
      },
    ],
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'Clinical Fitness',
    ownerName: 'Patrick Walker',
    pocName: 'Aisha Khan',
    pocTitle: 'Operations Lead',
    pocEmail: 'aisha@clinicalfitness.com',
    workflows: [
      {
        id: sid('wf'),
        name: 'Monthly Billing Automation',
        status: 'Pending BAA sign & SW access',
        stage: 'Customer Data Shared',
      },
    ],
    notes: 'Customer data shared 4/17.',
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'Bronwick',
    ownerName: 'Adam Bolton',
    pocName: 'Ben Foster',
    pocTitle: 'Talent Director',
    pocEmail: 'ben.foster@bronwick.com',
    secondaryContacts: [
      { id: sid('poc'), name: 'Dana Cole', title: 'Recruiting Ops', email: 'dana@bronwick.com' },
    ],
    workflows: [
      {
        id: sid('wf'),
        name: 'Candidate Monitoring',
        status: 'Proposal review',
        stage: 'Sales Review',
      },
      {
        id: sid('wf'),
        name: 'Prospecting',
        status: 'Proposal review',
        stage: 'Sales Review',
      },
    ],
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'Marketing Nerdy',
    ownerName: 'Grant Walker',
    pocName: 'Ian',
    workflows: [
      {
        id: sid('wf'),
        name: 'Persona Gen',
        status: 'Build for Ian + Client',
        stage: 'Initial Solutioning',
      },
    ],
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'Infrared',
    ownerName: 'Grant Walker',
    pocName: 'Scott',
    workflows: [
      {
        id: sid('wf'),
        name: 'Ops workflow, back office',
        status: 'Discovery w/Grant + Scott',
        stage: 'Discovery',
      },
    ],
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'Coldies',
    ownerName: 'Trenton Hughes',
    workflows: [
      { id: sid('wf'), name: 'Content engine', stage: 'Discovery' },
      { id: sid('wf'), name: 'Invoice automation, billing', stage: 'Discovery' },
      { id: sid('wf'), name: 'GTM', stage: 'Discovery' },
    ],
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'Argo IQ',
    ownerName: 'Trenton Hughes',
    pocName: 'Adam Bolton',
    pocTitle: 'Founder',
    pocEmail: 'adam@argoiq.com',
    workflows: [
      { id: sid('wf'), name: 'Content engine', stage: 'Initial Solutioning' },
    ],
    notes: 'Internal — dogfooding the content engine.',
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('acct'),
    name: 'Argus',
    ownerName: 'Patrick Walker',
    workflows: [
      {
        id: sid('wf'),
        name: 'Real estate stuff',
        status: 'Pending POC availability',
        stage: 'Discovery',
      },
      { id: sid('wf'), name: 'Back office, billing', stage: 'Discovery' },
    ],
    audit: auditCreated('Imported from Client Lifecycle tracker'),
    emails: [],
    createdAt: TS,
    updatedAt: TS,
  },
];

// ===========================================================================
// AppUsers — seed for Admin > Users
// ===========================================================================
//
// Ids match the mock AuthProvider so the role pill on top-right (and
// the user's last-sign-in timestamp) line up. Once Firebase Auth is
// live, the import script provisions Auth users first and writes their
// real uids back here.

// ===========================================================================
// Documents — proposals & contracts across the lifecycle statuses so the
// Contracts view has real records to filter/open/export. Bodies are short
// but real ArgoIQ-voice markdown; `renderBrandedDocHtml` wraps them in the
// branded cover/print chrome on export. Loaded once via
// bootstrapDocumentsIfEmpty(). `entityId`s are synthetic (seed-scoped) —
// the global Contracts view renders standalone via the denormalized
// `entityName`.
// ===========================================================================

const doc = (
  partial: Omit<BusinessDoc, 'createdAt' | 'updatedAt' | 'templateId'> & {
    createdAt?: string;
    updatedAt?: string;
  },
): BusinessDoc => ({
  templateId: partial.kind === 'proposal' ? 'proposal-default' : 'contract-default',
  createdBy: 'System',
  createdAt: TS,
  updatedAt: TS,
  ...partial,
});

export const seedDocuments: BusinessDoc[] = [
  doc({
    id: sid('doc'),
    kind: 'proposal',
    title: 'Proposal — Drift Peptides',
    status: 'sent',
    entityType: 'account',
    entityId: 'seed-acct-drift',
    entityName: 'Drift Peptides',
    value: 60_000,
    ownerName: 'Trenton Hughes',
    validUntil: '2026-06-30',
    body: [
      '## Executive summary',
      '',
      'Drift Peptides is scaling creator discovery faster than its current tooling can keep up with. Argo IQ deploys an AI operating layer — agents, an AQI scoring engine, and managed delivery — that compounds output instead of adding headcount.',
      '',
      'This proposal outlines a 12-month engagement measured against a single number: your **AQI score**.',
      '',
      '## Scope & approach',
      '',
      '1. **Discovery & AQI baseline** — audit current systems and set the target.',
      '2. **Agent deployment** — ticket, research, and outreach agents on real data.',
      '3. **Integration & enablement** — connect CRM, comms, and knowledge base.',
      '4. **Optimization** — weekly reviews against the AQI score.',
      '',
      '## Investment',
      '',
      '| Component | Amount |',
      '| --- | --- |',
      '| Activation & onboarding (one-time) | $15,000 |',
      '| Managed engagement (12 months) | $3,800 / mo |',
      '| **Total 12-month value** | **$60,000** |',
      '',
      '## Our guarantee',
      '',
      '> If your AQI score has not measurably improved within the first 90 days, we keep working at no additional cost until it does.',
    ].join('\n'),
  }),
  doc({
    id: sid('doc'),
    kind: 'contract',
    title: 'Service Agreement — Sam\u2019s List',
    status: 'signed',
    entityType: 'account',
    entityId: 'seed-acct-sams-list',
    entityName: 'Sam\u2019s List',
    value: 48_000,
    ownerName: 'Grant Walker',
    body: [
      '**Between:** Argo IQ, Inc. ("Provider") and Sam\u2019s List ("Client")',
      '**Effective date:** April 1, 2026',
      '',
      '## 1. Services',
      '',
      'Provider will deliver the Argo OS platform and managed AI-operations services, including agent deployment, integration, and ongoing optimization against the Client\u2019s AQI score.',
      '',
      '## 2. Term',
      '',
      'Initial term of 12 months, renewing in successive 12-month terms unless either party gives 30 days\u2019 written notice.',
      '',
      '## 3. Fees & payment',
      '',
      '| Item | Amount | Schedule |',
      '| --- | --- | --- |',
      '| Activation & onboarding | $12,000 | Due on signature |',
      '| Managed engagement | $3,000 / mo | Monthly, net 15 |',
      '',
      'Total committed value over the initial term is **$48,000**.',
    ].join('\n'),
  }),
  doc({
    id: sid('doc'),
    kind: 'proposal',
    title: 'Proposal — Karmic',
    status: 'viewed',
    entityType: 'account',
    entityId: 'seed-acct-karmic',
    entityName: 'Karmic',
    value: 36_000,
    ownerName: 'Trenton Hughes',
    validUntil: '2026-07-15',
    body: [
      '## Executive summary',
      '',
      'Karmic\u2019s CQS optimizer is ready for an operating layer that monitors quality continuously rather than in batches. Argo IQ stands up that layer and is accountable for the outcome alongside your team.',
      '',
      '## What you get',
      '',
      '- A deployed Argo OS workspace tailored to Karmic',
      '- Always-on agents that surface what needs attention before it slips',
      '- An AQI scorecard reviewed every sprint',
      '',
      '## Investment',
      '',
      '| Component | Amount |',
      '| --- | --- |',
      '| **Total 12-month value** | **$36,000** |',
    ].join('\n'),
  }),
  doc({
    id: sid('doc'),
    kind: 'contract',
    title: 'Service Agreement — Optimal Data',
    status: 'draft',
    entityType: 'account',
    entityId: 'seed-acct-optimal',
    entityName: 'Optimal Data',
    value: 54_000,
    ownerName: 'Grant Walker',
    body: [
      '**Between:** Argo IQ, Inc. ("Provider") and Optimal Data ("Client")',
      '**Effective date:** _pending counter-signature_',
      '',
      '## 1. Services',
      '',
      'Provider will deliver the LinkedIn Content Engine and managed AI-operations services as described in the accompanying proposal.',
      '',
      '## 3. Fees & payment',
      '',
      'Total committed value over the initial term is **$54,000**.',
    ].join('\n'),
  }),
  doc({
    id: sid('doc'),
    kind: 'proposal',
    title: 'Proposal — DevSignal',
    status: 'accepted',
    entityType: 'account',
    entityId: 'seed-acct-devsignal',
    entityName: 'DevSignal',
    value: 42_000,
    ownerName: 'Trenton Hughes',
    validUntil: '2026-05-30',
    body: [
      '## Executive summary',
      '',
      'DevSignal\u2019s parody-persona program proved the channel; this engagement turns it into an always-on content + outreach operating layer.',
      '',
      '## Next steps',
      '',
      '1. Align on scope and the target AQI score',
      '2. Counter-sign to kick off discovery',
      '3. First agents live within the opening sprint',
      '',
      '## Investment',
      '',
      '| Component | Amount |',
      '| --- | --- |',
      '| **Total 12-month value** | **$42,000** |',
    ].join('\n'),
  }),
];

// ===========================================================================
// Tickets — example tickets across statuses/priorities, including a
// `claude:` agent owner. Loaded once via bootstrapTicketsIfEmpty().
// ===========================================================================

const tEvent = (
  kind: TicketEvent['kind'],
  actor: string,
  text: string,
  at = TS,
): TicketEvent => ({ id: sid('tev'), at, actor, kind, text });

export const seedTickets: Ticket[] = [
  {
    id: 'TIQ-001',
    title: 'Wire Gmail MCP into account email trail',
    spec:
      'Replace the hand-edited email entries on the Account detail view with\n' +
      'a live pull from the Gmail MCP. Read-only to start: list the latest 10\n' +
      'messages for the account POC, newest first, mapped to EmailEntry.',
    status: 'in-progress',
    priority: 'p1',
    owner: 'claude:worker',
    createdBy: 'Grant',
    estimateHours: 6,
    deadline: '2026-06-10',
    startedAt: '2026-05-28T15:00:00.000Z',
    labels: ['integration', 'gmail'],
    timeline: [
      tEvent('created', 'Grant', 'Created TIQ-001'),
      tEvent('claimed', 'claude:worker', 'Claimed by claude:worker', '2026-05-28T14:30:00.000Z'),
      tEvent('status', 'claude:worker', 'Status: claimed → in-progress', '2026-05-28T15:00:00.000Z'),
    ],
    comments: [],
    createdAt: TS,
    updatedAt: '2026-05-28T15:00:00.000Z',
  },
  {
    id: 'TIQ-002',
    title: 'Kanban: persist column collapse state',
    spec:
      'Collapsing a pipeline column on the Workflows board should survive a\n' +
      'reload. Persist the collapsed-column set to localStorage and rehydrate\n' +
      'on mount.',
    status: 'open',
    priority: 'p2',
    owner: null,
    createdBy: 'Trent',
    estimateHours: 2,
    deadline: null,
    startedAt: null,
    labels: ['kanban', 'ux'],
    timeline: [tEvent('created', 'Trent', 'Created TIQ-002')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-003',
    title: 'Overdue deadline highlighting on ticket rows',
    spec:
      'Ticket rows whose deadline is in the past (and status is not done /\n' +
      'cancelled) should render the deadline in the warm/red accent so they\n' +
      'stand out in the board.',
    status: 'review',
    priority: 'p2',
    owner: 'Trent',
    createdBy: 'Grant',
    estimateHours: 1,
    deadline: '2026-05-30',
    startedAt: '2026-05-29T18:00:00.000Z',
    labels: ['tickets', 'ux'],
    timeline: [
      tEvent('created', 'Grant', 'Created TIQ-003'),
      tEvent('assigned', 'Grant', 'Owner: — → Trent'),
      tEvent('status', 'Trent', 'Status: open → in-progress', '2026-05-29T18:00:00.000Z'),
      tEvent('status', 'Trent', 'Status: in-progress → review', '2026-05-30T12:00:00.000Z'),
    ],
    comments: [
      {
        id: sid('tcm'),
        at: '2026-05-30T12:05:00.000Z',
        author: 'Trent',
        body: 'Ready for a quick look — used C.orangeWarm for the overdue state.',
      },
    ],
    createdAt: TS,
    updatedAt: '2026-05-30T12:05:00.000Z',
  },
  {
    id: 'TIQ-004',
    title: 'Research: Firestore composite indexes for tickets board',
    spec:
      'Figure out which composite indexes the tickets board will need once we\n' +
      'cut over to Firestore (status + updatedAt, owner + status). Write up a\n' +
      'short note and a firestore.indexes.json draft.',
    status: 'backlog',
    priority: 'p3',
    owner: 'claude:researcher',
    createdBy: 'Grant',
    estimateHours: 3,
    deadline: null,
    startedAt: null,
    labels: ['research', 'firestore'],
    timeline: [
      tEvent('created', 'Grant', 'Created TIQ-004'),
      tEvent('assigned', 'Grant', 'Owner: — → claude:researcher'),
    ],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-005',
    title: 'Fix: proposal export drops the engagement-value badge in Safari',
    spec:
      'On the branded proposal export, the cover "Engagement value" badge\n' +
      'renders blank in Safari (works in Chrome). Reproduce, isolate the\n' +
      'print/color-adjust rule, and land a fix that holds across both.',
    status: 'open',
    priority: 'p1',
    owner: null,
    createdBy: 'Grant',
    estimateHours: 3,
    deadline: '2026-06-09',
    startedAt: null,
    labels: ['bug', 'proposals', 'export'],
    timeline: [tEvent('created', 'Grant', 'Created TIQ-005')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-006',
    title: 'Add CSV export to the prospects table',
    spec:
      'Build a "Export CSV" action on the Prospects view that streams the\n' +
      'current filtered/sorted rows (name, company, score, stage, owner,\n' +
      'follow-up date) to a download. No backend — generate client-side.',
    status: 'open',
    priority: 'p2',
    owner: null,
    createdBy: 'Trent',
    estimateHours: 4,
    deadline: null,
    startedAt: null,
    labels: ['build', 'crm', 'export'],
    timeline: [tEvent('created', 'Trent', 'Created TIQ-006')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-007',
    title: 'Research: best-fit enrichment provider for LinkedIn signals',
    spec:
      'Compare candidate enrichment APIs (coverage, signal quality, pricing,\n' +
      'rate limits) for the prospect-enrichment seam. Write up a short\n' +
      'recommendation and the fields we can reliably map to person.enrichment.',
    status: 'backlog',
    priority: 'p2',
    owner: null,
    createdBy: 'Grant',
    estimateHours: 3,
    deadline: null,
    startedAt: null,
    labels: ['research', 'enrichment'],
    timeline: [tEvent('created', 'Grant', 'Created TIQ-007')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-008',
    title: 'Wire Google Meet notes sync into the account timeline',
    spec:
      'Connect the meetNotes seam to the Account detail view so "Sync Google\n' +
      'Meet notes" pulls recent meeting notes for the account POC and appends\n' +
      'them to the activity timeline, newest first. Simulation fallback stays.',
    status: 'open',
    priority: 'p1',
    owner: null,
    createdBy: 'Trent',
    estimateHours: 5,
    deadline: '2026-06-12',
    startedAt: null,
    labels: ['integration', 'gsuite', 'build'],
    timeline: [tEvent('created', 'Trent', 'Created TIQ-008')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-009',
    title: 'Fix: kanban column count badge goes stale after drag',
    spec:
      'After dragging a card between pipeline columns, the per-column count\n' +
      'badge does not update until reload. Recompute counts from the live\n' +
      'column state on drop.',
    status: 'open',
    priority: 'p3',
    owner: null,
    createdBy: 'Grant',
    estimateHours: 2,
    deadline: null,
    startedAt: null,
    labels: ['bug', 'kanban', 'ux'],
    timeline: [tEvent('created', 'Grant', 'Created TIQ-009')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },

  // -------------------------------------------------------------------------
  // The 9 founder-feedback asks, each broken out as its own ticket so the
  // Claude worker (and the team) can work them for real. Tagged `feedback`
  // and routed across the claude:* specialties. These are the canonical
  // backlog that the autopilot picks up — `runVerb`/skill keywords on the
  // agents match the labels below.
  // -------------------------------------------------------------------------
  {
    id: 'TIQ-101',
    title: 'Make the chat agent genuinely smart & easy to interact with',
    spec:
      'The chat agent should feel like talking to a capable operator, not a\n' +
      'rigid command box. Broaden intent coverage (create/update contacts,\n' +
      'deals, tasks, follow-ups, documents, tickets), parse multi-clause\n' +
      'messages into several proposed actions, resolve names fuzzily against\n' +
      'the live store, support pronoun follow-ups ("draft a proposal for\n' +
      'her"), and reply with a capabilities/help summary on an unknown or\n' +
      'empty message. When the Claude worker is configured, route /interpret\n' +
      'through it; keep the local parser as the offline fallback.',
    status: 'open',
    priority: 'p1',
    owner: null,
    createdBy: 'Trent',
    estimateHours: 8,
    deadline: '2026-06-13',
    startedAt: null,
    labels: ['feedback', 'chat', 'agent'],
    timeline: [tEvent('created', 'Trent', 'Created TIQ-101 — chat agent')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-102',
    title: 'Smart CRM completeness — fields, dropdowns, follow-ups, enrichment',
    spec:
      'Make the CRM actually smart: add the missing structured fields\n' +
      '(LinkedIn, website, multi-POC, deliverables, billing dates, renewal),\n' +
      'replace free-text inputs with dropdowns (source, owner, stage,\n' +
      'status), surface auto follow-up reminders (overdue / due-soon) on the\n' +
      'Dashboard and a CRM badge, and add prospect enrichment that evaluates\n' +
      'a LinkedIn URL + website into a short signal summary on the contact.',
    status: 'open',
    priority: 'p1',
    owner: null,
    createdBy: 'Grant',
    estimateHours: 10,
    deadline: '2026-06-16',
    startedAt: null,
    labels: ['feedback', 'crm', 'build'],
    timeline: [tEvent('created', 'Grant', 'Created TIQ-102 — smart CRM')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-103',
    title: 'Richer persona creator matching the ArgoIQ personas site',
    spec:
      'Expand the persona model from {archetype, summary} to a full ICP:\n' +
      'role, seniority, company profile, goals, pain points, motivations,\n' +
      'objections, decision criteria, channels, KPIs, and a representative\n' +
      'quote. Rebuild the persona card + editor to render the structured\n' +
      'persona on-brand, matching trenton.personas.argoiq.com. Keep the\n' +
      '"Generate from CRM" path working.',
    status: 'open',
    priority: 'p2',
    owner: null,
    createdBy: 'Trent',
    estimateHours: 8,
    deadline: null,
    startedAt: null,
    labels: ['feedback', 'personas', 'crm'],
    timeline: [tEvent('created', 'Trent', 'Created TIQ-103 — personas')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-104',
    title: 'Proposals that look like the ArgoIQ proposal site',
    spec:
      'Generated proposals should match proposals.argoiq.com/fusiontaxes:\n' +
      'dark hero/cover → problem → findings → solution → timeline → pricing →\n' +
      'guarantee → accept-CTA → footer, on-brand type + accents, decorative\n' +
      'diamonds, and a clean print stylesheet. Map the markdown template onto\n' +
      'those sections and keep Export-PDF / Download-.md working.',
    status: 'open',
    priority: 'p2',
    owner: null,
    createdBy: 'Grant',
    estimateHours: 6,
    deadline: null,
    startedAt: null,
    labels: ['feedback', 'proposals', 'docs'],
    timeline: [tEvent('created', 'Grant', 'Created TIQ-104 — proposals')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-105',
    title: 'Real, visible Claude ticket-taking (not simulation)',
    spec:
      'The autopilot should connect to a real Claude worker that claims a\n' +
      'ticket, works it, and reports back — with a clear "Connected to Claude\n' +
      'worker" vs "Simulation mode" status pill so it is obvious which is\n' +
      'live. Wire autopilotTick() through argoWorker.runAgent() when\n' +
      'VITE_AGENT_API_URL is set; keep the deterministic simulation as the\n' +
      'offline fallback. Agent-attributed comments must read as the Claude\n' +
      'agent working the ticket.',
    status: 'open',
    priority: 'p1',
    owner: null,
    createdBy: 'Trent',
    estimateHours: 8,
    deadline: '2026-06-11',
    startedAt: null,
    labels: ['feedback', 'agents', 'worker'],
    timeline: [tEvent('created', 'Trent', 'Created TIQ-105 — real ticket-taking')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-106',
    title: 'Build the missing agents (ingestion / content / ops)',
    spec:
      'Add the agent templates we discussed that do not exist yet: ingestion\n' +
      '(market-research, icp-builder, web-scraper, linkedin-monitor,\n' +
      'x-monitor, six-sigma-analyst), content (content-writer,\n' +
      'outreach-sequencer), and ClickUp-style ops (task-router,\n' +
      'deadline-watcher, standup-reporter). Each needs a blurb, persona,\n' +
      'cadence, description, and runVerb; seed a couple as deployed instances.',
    status: 'open',
    priority: 'p2',
    owner: null,
    createdBy: 'Grant',
    estimateHours: 6,
    deadline: null,
    startedAt: null,
    labels: ['feedback', 'agents'],
    timeline: [tEvent('created', 'Grant', 'Created TIQ-106 — missing agents')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-107',
    title: 'Real Development tab — north star, roadmap, milestones, docs',
    spec:
      'Replace the Development placeholder with a real hub: a north-star\n' +
      'statement, a roadmap (stages → goals → milestones with owners +\n' +
      'timelines), a milestones board (status, owner, due date, linked\n' +
      'tickets/tasks), and an important-docs / worker-links section that\n' +
      'surfaces the worker + integration seam statuses.',
    status: 'open',
    priority: 'p2',
    owner: null,
    createdBy: 'Trent',
    estimateHours: 7,
    deadline: null,
    startedAt: null,
    labels: ['feedback', 'development'],
    timeline: [tEvent('created', 'Trent', 'Created TIQ-107 — development tab')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-108',
    title: 'GSuite / Google Meet notes into contact & account timelines',
    spec:
      'Add a meetNotes seam (mirrors argoWorker): VITE_GSUITE_API_URL flag,\n' +
      'isConfigured(), pullMeetingNotes(context) → notes mapped to\n' +
      'contact/account activity entries, with a deterministic simulation when\n' +
      'unconfigured. Surface a "Sync Google Meet notes" action + status pill\n' +
      'on account/contact detail.',
    status: 'open',
    priority: 'p2',
    owner: null,
    createdBy: 'Grant',
    estimateHours: 6,
    deadline: null,
    startedAt: null,
    labels: ['feedback', 'integration', 'gsuite'],
    timeline: [tEvent('created', 'Grant', 'Created TIQ-108 — meet notes')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'TIQ-109',
    title: 'Polish: ⌘K quick-add, attention rollup, unified activity',
    spec:
      'The "what else would make it amazing" pass: a global ⌘K command\n' +
      'palette / quick-add that routes into the chat agent, a Dashboard\n' +
      '"what needs attention today" rollup (overdue follow-ups, stale deals,\n' +
      'due deliverables, upcoming billing), and a unified activity timeline\n' +
      'across contacts / accounts / tickets.',
    status: 'open',
    priority: 'p3',
    owner: null,
    createdBy: 'Trent',
    estimateHours: 6,
    deadline: null,
    startedAt: null,
    labels: ['feedback', 'polish'],
    timeline: [tEvent('created', 'Trent', 'Created TIQ-109 — amazing polish')],
    comments: [],
    createdAt: TS,
    updatedAt: TS,
  },
];

// ===========================================================================
// Project tasks — example PM tasks across statuses, assignees, due
// dates, checklists, and a recurring task. Loaded once via
// bootstrapTasksIfEmpty().
// ===========================================================================

const taskEvent = (
  kind: TaskEvent['kind'],
  actor: string,
  text: string,
  at = TS,
): TaskEvent => ({ id: sid('tkev'), at, actor, kind, text });

// ===========================================================================
// Projects — the Clients → Projects → Tasks middle layer. Each links to a
// seed Account by id (looked up by name so it survives seq re-ordering) and
// groups the seed tasks below. Loaded once via bootstrapProjectsIfEmpty().
// ===========================================================================

const acctIdByName = (name: string): string =>
  seedAccounts.find((a) => a.name === name)?.id ?? name;

export const seedProjects: Project[] = [
  {
    id: sid('proj'),
    clientId: acctIdByName('Drift Peptides'),
    clientName: 'Drift Peptides',
    title: 'Analytics Dashboard v1',
    description: 'First customer-facing analytics surface, fed by the feedback loop.',
    status: 'active',
    priority: 'high',
    dueDate: '2026-06-08',
    tags: ['delivery', 'analytics'],
    ownerName: 'Trent',
    memberNames: ['Trent', 'Grant'],
    links: [],
    comments: [],
    attachments: [],
    createdBy: 'Grant',
    timeline: [taskEvent('created', 'Grant', 'Created project')],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('proj'),
    clientId: acctIdByName('Bronwick'),
    clientName: 'Bronwick',
    title: 'Proposal & Prospecting',
    description: 'Land Bronwick: proposal v2 plus the prospecting workflow build-out.',
    status: 'active',
    priority: 'urgent',
    dueDate: '2026-06-04',
    tags: ['sales', 'proposal'],
    ownerName: 'Grant',
    memberNames: ['Grant'],
    links: [],
    comments: [],
    attachments: [],
    createdBy: 'Grant',
    timeline: [taskEvent('created', 'Grant', 'Created project')],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('proj'),
    clientId: acctIdByName('Clinical Fitness'),
    clientName: 'Clinical Fitness',
    title: 'Billing Automation',
    description: 'Monthly billing automation rollout — currently blocked on the BAA.',
    status: 'on-hold',
    priority: 'high',
    dueDate: '2026-05-30',
    tags: ['delivery', 'billing'],
    ownerName: 'Grant',
    memberNames: ['Grant', 'Patrick'],
    links: [],
    comments: [],
    attachments: [],
    createdBy: 'Grant',
    timeline: [taskEvent('created', 'Grant', 'Created project')],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('proj'),
    clientId: acctIdByName("Sam's List"),
    clientName: "Sam's List",
    title: 'Client Outreach System',
    description: 'Automated outreach monitoring for Sam’s List.',
    status: 'active',
    priority: 'medium',
    dueDate: null,
    tags: ['delivery', 'outreach'],
    ownerName: 'Grant',
    memberNames: ['Grant'],
    links: [],
    comments: [],
    attachments: [],
    createdBy: 'Grant',
    timeline: [taskEvent('created', 'Grant', 'Created project')],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('proj'),
    clientId: acctIdByName('Argo IQ'),
    clientName: 'Argo IQ',
    title: 'Internal Ops',
    description: 'Recurring internal delivery cadence — the weekly lifecycle sync lives here.',
    status: 'active',
    priority: 'medium',
    dueDate: null,
    tags: ['internal'],
    ownerName: 'Patrick',
    memberNames: ['Patrick', 'Grant', 'Trent'],
    links: [],
    comments: [],
    attachments: [],
    createdBy: 'Patrick',
    timeline: [taskEvent('created', 'Patrick', 'Created project')],
    createdAt: TS,
    updatedAt: TS,
  },
];

export const seedTasks: ProjectTask[] = [
  {
    id: sid('task'),
    title: 'Ship Drift Peptides analytics dashboard v1',
    projectId: seedProjects[0].id,
    projectName: seedProjects[0].title,
    notes: 'First customer-facing analytics view. Pull from the feedback loop.',
    status: 'in-progress',
    priority: 'high',
    assignee: 'Trent',
    dueDate: '2026-06-08',
    recurrence: 'none',
    checklist: [
      { id: sid('ck'), text: 'Wire data source', done: true },
      { id: sid('ck'), text: 'Build chart components', done: true },
      { id: sid('ck'), text: 'Review with Grant', done: false },
    ],
    accountName: 'Drift Peptides',
    workflowName: 'Analytics Feedback Loop',
    completedAt: null,
    createdBy: 'Grant',
    timeline: [
      taskEvent('created', 'Grant', 'Created task'),
      taskEvent('assigned', 'Grant', 'Assignee: — → Trent'),
      taskEvent('status', 'Trent', 'Status: todo → in-progress', '2026-05-29T15:00:00.000Z'),
    ],
    createdAt: TS,
    updatedAt: '2026-05-29T15:00:00.000Z',
  },
  {
    id: sid('task'),
    title: 'Draft Bronwick proposal v2',
    projectId: seedProjects[1].id,
    projectName: seedProjects[1].title,
    notes: 'Incorporate feedback from sales review — tighten scope on prospecting.',
    status: 'todo',
    priority: 'urgent',
    assignee: 'Grant',
    dueDate: '2026-06-04',
    recurrence: 'none',
    checklist: [
      { id: sid('ck'), text: 'Pull pricing tiers', done: false },
      { id: sid('ck'), text: 'Add candidate-monitoring SOW', done: false },
    ],
    accountName: 'Bronwick',
    workflowName: 'Prospecting',
    completedAt: null,
    createdBy: 'Grant',
    timeline: [taskEvent('created', 'Grant', 'Created task')],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('task'),
    title: 'Weekly client lifecycle sync',
    projectId: seedProjects[4].id,
    projectName: seedProjects[4].title,
    notes: 'Review every account stage and surface blockers for the week.',
    status: 'todo',
    priority: 'medium',
    assignee: 'Patrick',
    dueDate: '2026-06-05',
    recurrence: 'weekly',
    checklist: [
      { id: sid('ck'), text: 'Review kanban movement', done: false },
      { id: sid('ck'), text: 'Note at-risk accounts', done: false },
    ],
    completedAt: null,
    createdBy: 'Patrick',
    timeline: [taskEvent('created', 'Patrick', 'Created task')],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('task'),
    title: 'Confirm Clinical Fitness BAA signature',
    projectId: seedProjects[2].id,
    projectName: seedProjects[2].title,
    notes: 'Blocked on legal review from their side.',
    status: 'blocked',
    priority: 'high',
    assignee: 'Grant',
    dueDate: '2026-05-30',
    recurrence: 'none',
    checklist: [],
    accountName: 'Clinical Fitness',
    workflowName: 'Monthly Billing Automation',
    completedAt: null,
    createdBy: 'Grant',
    timeline: [
      taskEvent('created', 'Grant', 'Created task'),
      taskEvent('status', 'Grant', 'Status: todo → blocked', '2026-05-27T12:00:00.000Z'),
    ],
    createdAt: TS,
    updatedAt: '2026-05-27T12:00:00.000Z',
  },
  {
    id: sid('task'),
    title: 'Set up Sam\u2019s List outreach monitoring',
    status: 'done',
    priority: 'medium',
    assignee: 'claude:worker',
    projectId: seedProjects[3].id,
    projectName: seedProjects[3].title,
    dueDate: '2026-05-25',
    recurrence: 'none',
    checklist: [
      { id: sid('ck'), text: 'Configure monitor', done: true },
      { id: sid('ck'), text: 'Smoke test', done: true },
    ],
    accountName: "Sam's List",
    workflowName: 'Client Outreach System',
    completedAt: '2026-05-24T18:00:00.000Z',
    createdBy: 'Grant',
    timeline: [
      taskEvent('created', 'Grant', 'Created task'),
      taskEvent('status', 'claude:worker', 'Status: todo → done', '2026-05-24T18:00:00.000Z'),
    ],
    createdAt: TS,
    updatedAt: '2026-05-24T18:00:00.000Z',
  },
];

// ===========================================================================
// Deployed agents — example agents launched from Projects → Agents.
// One continuous ticket worker (running) and one daily outreach monitor
// (idle, with a couple of runs logged). Loaded once via
// bootstrapAgentsIfEmpty().
// ===========================================================================

const agentEntry = (
  kind: AgentRunEntry['kind'],
  actor: string,
  text: string,
  at = TS,
): AgentRunEntry => ({ id: sid('agev'), at, actor, kind, text });

export const seedAgents: DeployedAgent[] = [
  {
    id: sid('agent'),
    name: 'Queue Worker',
    template: 'ticket-worker',
    persona: 'claude:worker',
    description:
      'Sweeps the ticket queue, claims unowned work that matches its skills, ' +
      'and advances it one step at a time toward review.',
    cadence: 'continuous',
    state: 'running',
    createdBy: 'Grant',
    lastRunAt: '2026-06-02T14:30:00.000Z',
    runCount: 18,
    timeline: [
      agentEntry('deployed', 'Grant', 'Deployed from Ticket worker template'),
      agentEntry(
        'run',
        'claude:worker',
        'Ran — swept the ticket queue and advanced in-flight work',
        '2026-06-02T14:30:00.000Z',
      ),
    ],
    createdAt: TS,
    updatedAt: '2026-06-02T14:30:00.000Z',
  },
  {
    id: sid('agent'),
    name: 'Prospect Watch',
    template: 'outreach-monitor',
    persona: 'claude:worker',
    description:
      'Scans prospects for stale follow-ups and overdue next-steps, then ' +
      'logs the ones that need a touch.',
    cadence: 'daily',
    state: 'idle',
    createdBy: 'Patrick',
    lastRunAt: '2026-06-02T09:00:00.000Z',
    runCount: 5,
    timeline: [
      agentEntry('deployed', 'Patrick', 'Deployed from Outreach monitor template'),
      agentEntry(
        'run',
        'claude:worker',
        'Ran — scanned prospects and flagged stale follow-ups',
        '2026-06-02T09:00:00.000Z',
      ),
    ],
    createdAt: TS,
    updatedAt: '2026-06-02T09:00:00.000Z',
  },
  {
    id: sid('agent'),
    name: 'Market Radar',
    template: 'market-research',
    persona: 'claude:researcher',
    description:
      'Monitors the competitive landscape — competitor moves, pricing changes, ' +
      'funding news, and category shifts — and writes up what changed and why it matters.',
    cadence: 'daily',
    state: 'running',
    createdBy: 'Grant',
    lastRunAt: '2026-06-03T07:00:00.000Z',
    runCount: 11,
    timeline: [
      agentEntry('deployed', 'Grant', 'Deployed from Market research template'),
      agentEntry(
        'run',
        'claude:researcher',
        'Ran — scanned the market and logged competitive movements',
        '2026-06-03T07:00:00.000Z',
      ),
    ],
    createdAt: TS,
    updatedAt: '2026-06-03T07:00:00.000Z',
  },
  {
    id: sid('agent'),
    name: 'Content Studio',
    template: 'content-writer',
    persona: 'claude:worker',
    description:
      'Drafts on-brand content — blog posts, sales emails, one-pagers, and proposal ' +
      'sections — from a brief and the surrounding CRM context.',
    cadence: 'manual',
    state: 'idle',
    createdBy: 'Patrick',
    lastRunAt: '2026-06-01T16:20:00.000Z',
    runCount: 7,
    timeline: [
      agentEntry('deployed', 'Patrick', 'Deployed from Content writer template'),
      agentEntry(
        'run',
        'claude:worker',
        'Ran — drafted on-brand content from the latest briefs',
        '2026-06-01T16:20:00.000Z',
      ),
    ],
    createdAt: TS,
    updatedAt: '2026-06-01T16:20:00.000Z',
  },
  {
    id: sid('agent'),
    name: 'Deadline Sentinel',
    template: 'deadline-watcher',
    persona: 'claude:researcher',
    description:
      'Watches due dates across tickets, tasks, deliverables, and billing, and ' +
      'escalates anything overdue or at risk of slipping.',
    cadence: 'hourly',
    state: 'running',
    // Ships live by default (the fleet's most-run agent): ticks on its
    // hourly cadence while the app is open. Safe-by-design — runs only
    // queue proposals, never auto-write.
    liveSchedule: true,
    createdBy: 'Adam',
    lastRunAt: '2026-06-03T11:00:00.000Z',
    runCount: 42,
    timeline: [
      agentEntry('deployed', 'Adam', 'Deployed from Deadline watcher template'),
      agentEntry(
        'run',
        'claude:researcher',
        'Ran — checked deadlines and escalated at-risk work',
        '2026-06-03T11:00:00.000Z',
      ),
    ],
    createdAt: TS,
    updatedAt: '2026-06-03T11:00:00.000Z',
  },
  {
    id: sid('agent'),
    name: 'Ops Sentinel',
    template: 'system-scanner',
    persona: 'claude:researcher',
    description:
      'Continuously sweeps tickets, tasks, and accounts for overdue deadlines, ' +
      'blocked work, and stalled deals, and surfaces what needs attention now.',
    cadence: 'continuous',
    state: 'running',
    createdBy: 'Grant',
    lastRunAt: '2026-06-03T12:00:00.000Z',
    runCount: 64,
    timeline: [
      agentEntry('deployed', 'Grant', 'Deployed from System scanner template'),
      agentEntry(
        'run',
        'claude:researcher',
        'Ran — scanned the system and surfaced items needing attention',
        '2026-06-03T12:00:00.000Z',
      ),
    ],
    createdAt: TS,
    updatedAt: '2026-06-03T12:00:00.000Z',
  },
  {
    id: sid('agent'),
    name: 'Task Router',
    template: 'task-router',
    persona: 'claude:worker',
    description:
      'Triages incoming tickets, matches unowned high-priority work to the human ' +
      'carrying the lightest load, and proposes the assignment for review.',
    cadence: 'continuous',
    state: 'running',
    createdBy: 'Grant',
    // Freshly deployed — no fabricated run history. The first real run logs an
    // honest narration of what the routing producer actually proposed.
    lastRunAt: null,
    runCount: 0,
    timeline: [agentEntry('deployed', 'Grant', 'Deployed from Task router template')],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('agent'),
    name: 'Success Sentinel',
    template: 'customer-success',
    persona: 'claude:worker',
    description:
      'Scores every live customer\u2019s health from their real record and proposes a ' +
      'check-in task for any account that slips into the watch or at-risk band, so no ' +
      'live customer goes quiet unnoticed.',
    cadence: 'daily',
    state: 'idle',
    createdBy: 'Grant',
    // Freshly deployed — no fabricated run history. The first real run logs an
    // honest narration of what the health producer actually proposed.
    lastRunAt: null,
    runCount: 0,
    timeline: [agentEntry('deployed', 'Grant', 'Deployed from Customer success template')],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('agent'),
    name: 'Document Chaser',
    template: 'document-chase',
    persona: 'claude:worker',
    description:
      'Reads the documents collection for proposals and contracts the client is ' +
      'sitting on \u2014 lapsed past validity (still inflating open pipeline) or awaiting ' +
      'the client with no movement \u2014 and proposes a chase task tied to the document\u2019s ' +
      'own account or contact so stale deals never quietly go cold.',
    cadence: 'daily',
    state: 'idle',
    createdBy: 'Grant',
    // Freshly deployed — no fabricated run history. The first real run logs an
    // honest narration of what the document-chase producer actually proposed.
    lastRunAt: null,
    runCount: 0,
    timeline: [agentEntry('deployed', 'Grant', 'Deployed from Document chaser template')],
    createdAt: TS,
    updatedAt: TS,
  },
];

export const seedAppUsers: AppUser[] = [
  {
    id: 'u-grant',
    email: 'grant@argoiq.com',
    displayName: 'Grant Walker',
    role: 'admin',
    status: 'active',
    notes: 'Founding admin. Owns Argo OS + platform.',
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'u-adam',
    email: 'adam@argoiq.com',
    displayName: 'Adam Bolton',
    role: 'admin',
    status: 'active',
    notes: 'Founding admin. Owns enterprise accounts.',
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'u-patrick',
    email: 'patrick@argoiq.com',
    displayName: 'Patrick Walker',
    role: 'staff',
    status: 'active',
    notes: 'Co-founder; product, design system, customer experience.',
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: 'u-viewer',
    email: 'viewer@argoiq.com',
    displayName: 'Read-only Viewer',
    role: 'viewer',
    status: 'active',
    notes: 'Demo viewer account — verifies read-only behavior in dev.',
    createdAt: TS,
    updatedAt: TS,
  },
];

// ===========================================================================
// Roadmap — the engineering north-star ladder. Stages: now / next / later /
// shipped. Each milestone names a goal it serves, an owner, a timeline, and a
// completion estimate so the Development tab tells the whole delivery story.
// ===========================================================================

export const seedRoadmap: RoadmapItem[] = [
  {
    id: sid('rm'),
    title: 'Agent operating layer GA',
    description:
      'Ship the deployable-agent catalogue, autopilot, and the hosted Claude ' +
      'worker seam so customers run real agents end to end.',
    stage: 'now',
    status: 'in-progress',
    goal: 'Agents that compound output',
    assignee: 'Grant Walker',
    startDate: '2026-05-12',
    targetDate: '2026-06-20',
    // With links present, progress is auto-computed from the linked
    // items' completion (src/lib/crm/milestones.ts) and this manual
    // estimate is ignored — kept for the no-links fallback path.
    progress: 70,
    linkedTicketIds: ['TIQ-101', 'TIQ-105', 'TIQ-106'],
    // Reference by index, not literal id — task ids come off the shared
    // sid() counter and shift whenever a seed is added above them.
    linkedTaskIds: [seedTasks[4].id],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'Smart CRM completeness',
    description:
      'LinkedIn/website enrichment, deliverables, billing dates, multi-POC, and ' +
      'auto follow-up reminders across the pipeline.',
    stage: 'now',
    status: 'in-progress',
    goal: 'A CRM that sells for you',
    assignee: 'Patrick Walker',
    startDate: '2026-05-19',
    targetDate: '2026-06-13',
    progress: 80,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'Branded proposals & contracts',
    description:
      'On-brand, exportable proposal/contract documents matching the ArgoIQ ' +
      'proposal site, generated from CRM context.',
    stage: 'now',
    status: 'done',
    goal: 'Close faster with less effort',
    assignee: 'Patrick Walker',
    startDate: '2026-05-05',
    targetDate: '2026-05-30',
    progress: 100,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'Google Workspace integration',
    description:
      'Pull Google Meet notes into the contact/account timeline automatically ' +
      'via a GSuite seam.',
    stage: 'next',
    status: 'planned',
    goal: 'Context with zero data entry',
    assignee: 'Adam Bolton',
    startDate: '2026-06-23',
    targetDate: '2026-07-18',
    progress: 0,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'AQI scoring engine v2',
    description:
      'Composite velocity/quality/coverage score with per-account trend lines ' +
      'and sprint-over-sprint deltas.',
    stage: 'next',
    status: 'planned',
    goal: 'Measure the outcome, not the tool',
    assignee: 'Grant Walker',
    startDate: '2026-07-07',
    targetDate: '2026-08-15',
    progress: 0,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'Multi-tenant workspaces',
    description:
      'Per-customer isolated workspaces with role-scoped access so Argo OS can ' +
      'be offered to clients directly.',
    stage: 'later',
    status: 'planned',
    goal: 'Argo OS as a product',
    assignee: 'Adam Bolton',
    startDate: '2026-08-18',
    targetDate: '2026-10-10',
    progress: 0,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'Mobile companion app',
    description:
      'Read-mostly mobile surface for the attention rollup, follow-ups, and ' +
      'agent activity on the go.',
    stage: 'later',
    status: 'planned',
    goal: 'Argo in your pocket',
    assignee: 'Patrick Walker',
    targetDate: '2026-Q4',
    progress: 0,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'Swappable agent worker seam',
    description:
      'Single network seam (argoWorker) so chat, autopilot, and deployed agents ' +
      'route to a hosted Claude worker when configured.',
    stage: 'shipped',
    status: 'done',
    goal: 'Agents that compound output',
    assignee: 'Grant Walker',
    startDate: '2026-04-21',
    targetDate: '2026-05-09',
    progress: 100,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },

  // --- Path to $6M ARR (company GTM track — dogfood) ------------------------
  // We run our own go-to-market inside Argo OS. These milestones mirror
  // ROADMAP-TO-6M.md so the company-level plan lives on the Development tab
  // next to the product roadmap, not in a doc nobody opens. The shared goal
  // theme ("Path to $6M ARR") groups them as one track distinct from the
  // feature milestones above.
  {
    id: sid('rm'),
    title: 'Honest-accounting QA gate in CI',
    description:
      'Deterministic test suite locks the trust invariants (simulation never ' +
      'presented as live) plus lead-scoring and follow-up logic; typecheck + ' +
      'tests + build run on every push/PR. Shipped: vitest suite + qa.yml.',
    stage: 'now',
    status: 'done',
    goal: 'Path to $6M ARR',
    assignee: 'Grant Walker',
    startDate: '2026-06-17',
    targetDate: '2026-06-17',
    progress: 100,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'One real agent loop live for a customer',
    description:
      'Close the loop end-to-end with the hosted Claude worker: a real ' +
      'follow-up / ticket-triage producer queues proposals a customer approves ' +
      'and writes. Proves the Starter wedge is more than a demo.',
    stage: 'now',
    status: 'in-progress',
    goal: 'Path to $6M ARR',
    assignee: 'Grant Walker',
    startDate: '2026-06-09',
    targetDate: '2026-07-04',
    progress: 60,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'Self-serve onboarding (< 1 day to first approved action)',
    description:
      'Sign-up to seeded workspace to first operator-approved agent action in ' +
      'under a day with no hand-holding, plus time-to-value telemetry. The ' +
      'top-of-funnel that makes the Starter tier scale.',
    stage: 'now',
    status: 'planned',
    goal: 'Path to $6M ARR',
    assignee: 'Adam Bolton',
    startDate: '2026-06-23',
    targetDate: '2026-07-25',
    progress: 0,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'Outcome-based metering on approved actions',
    description:
      'Meter and bill on approved agent outcomes (the approvals ledger already ' +
      'counts them). The defensible, expansion-driving revenue line — sell ' +
      'outcomes, not seats.',
    stage: 'next',
    status: 'planned',
    goal: 'Path to $6M ARR',
    assignee: 'Patrick Walker',
    startDate: '2026-07-28',
    targetDate: '2026-09-05',
    progress: 0,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
  {
    id: sid('rm'),
    title: 'Tiered self-serve pricing (Starter / Team / Pro)',
    description:
      'Wire the three tiers plus the outcome layer to real approvals counts and ' +
      'usage caps, so the pricing model in ROADMAP-TO-6M.md is enforced in-app.',
    stage: 'next',
    status: 'planned',
    goal: 'Path to $6M ARR',
    assignee: 'Patrick Walker',
    startDate: '2026-08-04',
    targetDate: '2026-09-19',
    progress: 0,
    linkedTicketIds: [],
    createdAt: TS,
    updatedAt: TS,
  },
];
